import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

export type UserRole = 'super_user' | 'admin' | 'full_user' | 'view_user' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signingOut: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, invitationCode?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  userRole: UserRole;
  tenantId: string | null;
  refreshUserRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchUserRole = async (userId: string) => {
    try {
      // First check if super_user (no tenant)
      const { data: superUserRole } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .is('tenant_id', null)
        .maybeSingle();

      if (superUserRole?.role === 'super_user') {
        setUserRole('super_user');
        setTenantId(null);
        return;
      }

      // Get user's primary tenant
      const { data: tenantAccess } = await supabase
        .from('user_tenant_access')
        .select('tenant_id')
        .eq('user_id', userId)
        .eq('is_primary', true)
        .maybeSingle();

      if (tenantAccess?.tenant_id) {
        setTenantId(tenantAccess.tenant_id);
        
        // Get role for this tenant
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('tenant_id', tenantAccess.tenant_id)
          .maybeSingle();

        if (roleData?.role) {
          setUserRole(roleData.role as UserRole);
        } else {
          setUserRole(null);
        }
      } else {
        // Fallback: check any role for user
        const { data: anyRole } = await supabase
          .from('user_roles')
          .select('role, tenant_id')
          .eq('user_id', userId)
          .maybeSingle();

        if (anyRole) {
          setUserRole(anyRole.role as UserRole);
          setTenantId(anyRole.tenant_id);
        } else {
          setUserRole(null);
          setTenantId(null);
        }
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole(null);
      setTenantId(null);
    }
  };

  const refreshUserRole = async () => {
    if (user) {
      await fetchUserRole(user.id);
    }
  };

  // Helper function to check user approval status
  const checkUserApproval = async (userId: string): Promise<boolean> => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('approved')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error checking approval:', error);
        return false;
      }
      
      return profile?.approved === true;
    } catch (error) {
      console.error('Error in checkUserApproval:', error);
      return false;
    }
  };

  // Helper to clear auth state and sign out
  const clearAuthState = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      console.log('Sign out during clear (ignored):', e);
    }
    setSession(null);
    setUser(null);
    setUserRole(null);
    setTenantId(null);
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          // Defer approval check to avoid deadlocks
          setTimeout(async () => {
            const isApproved = await checkUserApproval(session.user!.id);
            
            if (!isApproved) {
              console.log('User not approved, signing out');
              await clearAuthState();
              setLoading(false);
              return;
            }
            
            // User is approved - proceed normally
            setSession(session);
            setUser(session.user);
            fetchUserRole(session.user!.id);
            setLoading(false);
          }, 0);
        } else {
          setSession(null);
          setUser(null);
          setUserRole(null);
          setTenantId(null);
          setLoading(false);
        }
      }
    );

    // THEN check for an existing session
    supabase.auth.getSession()
      .then(async ({ data: { session }, error }) => {
        if (error) {
          console.error('Error getting session:', error);
          setLoading(false);
          return;
        }

        if (!session?.user) {
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        // Check approval before allowing access
        const isApproved = await checkUserApproval(session.user.id);
        
        if (!isApproved) {
          console.log('User not approved during session restore, signing out');
          await clearAuthState();
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session.user);
        setTimeout(() => {
          fetchUserRole(session.user!.id);
        }, 0);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to initialize auth:', error);
        setSession(null);
        setUser(null);
        setUserRole(null);
        setTenantId(null);
        setLoading(false);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;

      // Check if user is approved
      if (data.user) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('approved')
          .eq('id', data.user.id)
          .maybeSingle();

        if (profileError) throw profileError;

        if (profile && !profile.approved) {
          // Sign out the user immediately
          await supabase.auth.signOut();
          throw new Error('Your account is pending admin approval. Please wait for approval before signing in.');
        }
      }
      
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, invitationCode?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: invitationCode ? { invitation_code: invitationCode } : undefined,
          emailRedirectTo: redirectUrl,
        },
      });
      
      if (error) throw error;
      
      // Immediately sign out the user after signup
      // They need admin approval before they can access the system
      await supabase.auth.signOut();
      
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    // Set signing out state FIRST to trigger UI loading
    setSigningOut(true);

    // Store tenant slug before clearing state
    let tenantSlug: string | null = null;
    if (tenantId) {
      try {
        const { data } = await supabase
          .from('tenants')
          .select('slug')
          .eq('id', tenantId)
          .maybeSingle();
        tenantSlug = data?.slug || null;
      } catch (error) {
        console.log('Error fetching tenant slug:', error);
      }
    }

    // IMMEDIATELY clear all auth state FIRST (before API call)
    setUserRole(null);
    setTenantId(null);
    setSession(null);
    setUser(null);

    // Clear localStorage items that might persist user data
    localStorage.removeItem('selected_merchant_id');

    try {
      // Use local scope to just clear the current session
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // Ignore session not found errors - session might already be invalid
      console.log('Sign out error (ignored):', error);
    }

    // Reset signing out state
    setSigningOut(false);
    
    // Redirect to tenant-specific login page if available (using correct URL pattern)
    if (tenantSlug) {
      navigate(`/${tenantSlug}/auth`, { replace: true });
    } else {
      navigate('/auth', { replace: true });
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading,
      signingOut,
      signIn, 
      signUp, 
      signOut, 
      userRole,
      tenantId,
      refreshUserRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
